import { Image as ImageIcon, Download, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { useArticleHeaderImage } from '../hooks/useArticleHeaderImage';
import { ASPECT_RATIO_CSS, type HeaderImageData } from '../utils/headerImageStorage';

/**
 * Props for the ArticleHeaderImage component
 */
export interface ArticleHeaderImageProps {
  articleId: string;
  articleTitle: string;
  /** 'article' shows generate/regenerate buttons, 'export' shows download only */
  mode: 'article' | 'export';
  /** Callback when user wants to open the image generation wizard (article mode only) */
  onOpenWizard?: () => void;
  /** Optional: externally managed image data (for when parent controls the hook) */
  imageData?: HeaderImageData | null;
  /** Optional: externally managed loading state */
  isLoading?: boolean;
  /** Optional: indicates if image is currently being generated (vs just loading from storage) */
  isGenerating?: boolean;
  /** Optional: error message to display when image generation fails */
  error?: string | null;
  /** Optional: callback to retry failed image generation */
  onRetry?: () => void;
}

/**
 * ArticleHeaderImage Component
 * 
 * Displays either a skeleton placeholder (with generate button) or the generated
 * header image with action buttons (download, regenerate).
 * 
 * Can be used in two modes:
 * 1. Self-managed: Component manages its own useArticleHeaderImage hook
 * 2. Externally managed: Parent passes imageData and isLoading props
 * 
 * Requirements: 1.1, 1.2, 1.3, 1.4, 4.1, 5.1, 6.1, 6.2, 6.3, 8.2, 8.3
 */
export function ArticleHeaderImage({
  articleId,
  articleTitle,
  mode,
  onOpenWizard,
  imageData: externalImageData,
  isLoading: externalIsLoading,
  isGenerating: _isGenerating = false, // Kept for API compatibility
  error,
  onRetry,
}: Readonly<ArticleHeaderImageProps>) {
  // Use internal hook only if external data is not provided
  const internalHook = useArticleHeaderImage(articleId);

  // Use external data if provided, otherwise fall back to internal hook
  const imageData = externalImageData !== undefined ? externalImageData : internalHook.imageData;
  const isLoading = externalIsLoading !== undefined ? externalIsLoading : internalHook.isLoading;

  /**
   * Handle image download
   * Creates a temporary link element to trigger download with sanitized filename
   * Requirements: 4.2, 4.3
   */
  const handleDownload = () => {
    if (!imageData?.imageUrl) return;

    const link = document.createElement('a');
    link.href = imageData.imageUrl;

    // Sanitize filename: remove special chars, replace spaces with hyphens, limit length
    const sanitizedTitle = articleTitle
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .slice(0, 50);

    link.download = `${sanitizedTitle}-header.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Show loading skeleton while checking storage or generating image
  if (isLoading) {
    return (
      <div className="relative w-full aspect-video overflow-hidden rounded-lg border bg-slate-300 dark:bg-slate-700">
        {/* Shimmer effect */}
        <div
          className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/80 to-transparent dark:via-white/20"
          style={{ animation: 'shimmer 1.5s infinite' }}
        />
        {/* Centered image icon */}
        <div className="absolute inset-0 flex items-center justify-center">
          <ImageIcon className="h-12 w-12 text-slate-500 dark:text-slate-400" />
        </div>
      </div>
    );
  }

  // Show error state when image generation failed
  if (error) {
    return (
      <div className="relative w-full aspect-video rounded-lg border-2 border-destructive/50 bg-destructive/5 flex flex-col items-center justify-center gap-3">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-destructive text-center px-4">{error}</p>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
        )}
      </div>
    );
  }

  // No image exists - show appropriate empty state based on mode
  if (!imageData) {
    if (mode === 'article') {
      // Article mode: Show skeleton with centered generate button
      // Requirements: 1.1, 1.2, 1.3, 1.4
      return (
        <div className="relative w-full aspect-video rounded-lg overflow-hidden group">
          <Skeleton className="absolute inset-0" />
          <div className="absolute inset-0 flex items-center justify-center">
            <Button
              variant="secondary"
              className="gap-2 transition-transform group-hover:scale-105"
              onClick={onOpenWizard}
            >
              <ImageIcon className="h-4 w-4" />
              Generate Article Image
            </Button>
          </div>
        </div>
      );
    }

    // Export mode: Show subtle message directing user to Article page
    // Requirements: 8.3
    return (
      <div className="w-full aspect-video rounded-lg border-2 border-dashed border-muted-foreground/25 flex items-center justify-center">
        <p className="text-sm text-muted-foreground">
          Generate a header image on the Article page
        </p>
      </div>
    );
  }

  // Image exists - display with action buttons
  // Requirements: 4.1, 5.1, 6.1, 6.2, 6.3, 8.2
  const aspectRatioClass = ASPECT_RATIO_CSS[imageData.aspectRatio] || 'aspect-video';

  return (
    <div className={`relative w-full ${aspectRatioClass} rounded-lg overflow-hidden`}>
      <img
        src={imageData.imageUrl}
        alt={`Header visual for ${articleTitle}`}
        className="w-full h-full object-cover"
      />
      
      {/* Action buttons - always visible for touch and keyboard accessibility */}
      <div className="absolute bottom-3 right-3 flex gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={handleDownload}
          title="Download image"
        >
          <Download className="h-4 w-4" />
        </Button>
        
        {mode === 'article' && onOpenWizard && (
          <Button
            size="sm"
            variant="secondary"
            onClick={onOpenWizard}
            title="Regenerate image"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default ArticleHeaderImage;
